import { Injectable } from '@angular/core';
import { AppStore, SetUserProfileAction } from '@alfresco/aca-shared/store';
import { ActivatedRoute, NavigationStart, Params, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { GroupEntry, Group } from '@alfresco/js-api';
import { from, Observable } from 'rxjs';
import { AlfrescoApiService, AppConfigValues, AuthenticationService, CookieService, PeopleContentService } from '@alfresco/adf-core';
import { ECM_TICKET } from '../models/alden-constants';
import { SMTUsersService } from './users-service';
import { AldenService } from './alden.service';
import { Store } from '@ngrx/store';
import { HttpClient } from '@angular/common/http';

const REMEMBER_ME_COOKIE_KEY = 'ALFRESCO_REMEMBER_ME';
const REMEMBER_ME_UNTIL = 1000 * 60 * 60 * 24 * 30;

@Injectable({
    providedIn: 'root'
})
export class LoginService {

    constructor(
        private router: Router,
        private http: HttpClient,
        private cookieService: CookieService,
        private authenticationService: AuthenticationService,
        private usersService: SMTUsersService,
        private peopleContentService: PeopleContentService,
        private alfrescoApi: AlfrescoApiService,
        private aldenService: AldenService,
        private store: Store<AppStore>,
        private alfrescoApiService: AlfrescoApiService,
        private route: ActivatedRoute
    ) { 
        this.authenticationService.onLogout?.subscribe(() => {
            this.aldenService.profile = undefined;
            this.aldenService.personDetails = undefined;
        });
    }

    /**
     * Escucha los eventos de navegación para gestionar el ticket ECM en la URL.
     *
     * Este método se suscribe a los eventos del router y, ante cada `NavigationStart`,
     * analiza la URL y sus parámetros para verificar si contiene un ticket ECM válido.
     * Si `checkECMTicket` confirma que debe procesarse, se reconstruye la navegación
     * eliminando el ticket de la ruta pero preservando los parámetros y el fragmento.
     *
     * Finalmente, se asegura de mostrar el header de la aplicación una vez completado
     * el proceso, independientemente del resultado.
     */
    listenEcmTicket(): void {
        this.router.events.pipe(filter(event => event instanceof NavigationStart))
            .subscribe((event: NavigationStart) => {
                const url = event.url;
                const urlTree = this.router.parseUrl(url);
                const queryParams = urlTree.queryParams;

                this.checkECMTicket(url, queryParams).then((res) => {
                    if (res) {
                        const url = urlTree.root.children['primary']?.segments.map(segment => segment.path);
                        //console.debug("Ticket URL", url);
                        this.router.navigate(url, {
                            queryParams,
                            fragment: urlTree.fragment
                        });
                    }
                }).finally(() => this.aldenService.showHeader$.next(true));
            });
    }

    /**
     * Realiza el proceso de autenticación del usuario mediante MFA TOTP.
     *
     * Este método envía las credenciales del usuario al endpoint
     * `mfa/totp/login` del servicio de Alfresco, incluyendo opcionalmente
     * el token TOTP para completar la autenticación multifactor.
     *
     * Si la autenticación es correcta:
     *  - Guarda la preferencia `rememberMe` si corresponde.
     *  - Construye un objeto `ticket` con el identificador devuelto por Alfresco.
     *  - Devuelve el tipo de proveedor configurado y la imagen QR (si aplica).
     *
     * Si la autenticación falla:
     *  - Devuelve `{ type: 'forbidden' }` cuando el backend responde 403.
     *  - Devuelve `{ type: 'error' }` para cualquier otro error.
     *
     * @param username Nombre de usuario.
     * @param password Contraseña del usuario.
     * @param rememberMe (Opcional) Indica si debe recordarse la sesión.
     * @param token (Opcional) Token TOTP para autenticación multifactor.
     * @returns Observable con el resultado del proceso de login.
     */
    login(username: string, password: string, rememberMe?: boolean, token?: string): Observable<{ type: string; ticket: any; qrImage?: any }> {
        const path = "smtecno/ssm/mfa/totp/login";
        const httpMethod = 'POST';
        const responseType = '';
        const url = 'alfresco/service';
        const pathParams = {};
        const postBody = {
            username: username,
            password: password,
            token: token,
        };
        const queryParams = {};
        const headerParams = {};
        const formParam = {};

        const contentTypes = ['application/json'];
        const accepts = ['application/json'];

        return from(
            this.alfrescoApi.getInstance()
                .contentClient.callApi(path, httpMethod, pathParams, queryParams, headerParams, formParam, postBody, contentTypes, accepts, responseType, url)
                .then((response: any) => {
                        this.saveRememberMeCookie(rememberMe);
                        const ticket = {
                            entry: {
                                id: response.data.ticket,
                                userid: username
                            }
                        };
                        return {
                            type: this.aldenService.getAppConfigProp(AppConfigValues.PROVIDERS),
                            ticket: ticket || null,
                            qrImage: response.data.dataUri
                        };
                    },
                    (error) => {
                        if (error && error.status === 403) {
                            return {
                                type: 'forbidden',
                                ticket: null
                            };
                        }
                        return {
                            type: 'error',
                            ticket: null
                        };
                    }
                )
        );
    }

    /**
     * Solicita el envío del correo de configuración TOTP para MFA.
     *
     * Este método realiza una llamada al endpoint `/mfa/totp/setup/email` del
     * servicio de Alfresco, enviando las credenciales del usuario para iniciar
     * el proceso de configuración del segundo factor basado en TOTP.
     *
     * Si la operación es exitosa, devuelve un objeto con `status: 200` y el
     * contenido de la respuesta del backend. En caso de error, devuelve un
     * objeto con `status: 500` y el mensaje de error correspondiente.
     *
     * @param username Nombre de usuario.
     * @param password Contraseña del usuario.
     * @returns Observable con el resultado del envío del correo de configuración TOTP.
     */
    sendTotpSetupEmail(username: string, password: string): Observable<{ status: number; message: any }> {
        const path = "smtecno/ssm/mfa/totp/setup/email";
        const httpMethod = 'POST';
        const responseType = '';
        const url = 'alfresco/service';
        const pathParams = {};
        const postBody = {
            username: username,
            password: password
        };
        const queryParams = {};
        const headerParams = {};
        const formParam = {};

        const contentTypes = ['application/json'];
        const accepts = ['application/json'];

        return from(
            this.alfrescoApi.getInstance()
                .contentClient.callApi(path, httpMethod, pathParams, queryParams, headerParams, formParam, postBody, contentTypes, accepts, responseType, url)
                .then(
                    (response: any) => {
                        let resp = {
                            status: 200,
                            message: response
                        }
                        return resp;
                    },
                    (err) => {
                        let error = {
                            status: 500,
                            message: err.message
                        }
                        return error;
                    }
                )
        );
    }

    /**
     * Guarda la cookie "remember me" como cookie persistente o de sesión.
     *
     * Si `rememberMe` es true, se genera una fecha de expiración futura para
     * crear una cookie de larga duración. En caso contrario, la cookie se
     * guarda como cookie de sesión (sin fecha de expiración explícita).
     *
     * @param rememberMe Indica si debe crearse una cookie persistente.
     */
    private saveRememberMeCookie(rememberMe: boolean): void {
        let expiration = null;

        if (rememberMe) {
            expiration = new Date();
            const time = expiration.getTime();
            const expireTime = time + REMEMBER_ME_UNTIL;
            expiration.setTime(expireTime);
        }
        this.cookieService.setItem(REMEMBER_ME_COOKIE_KEY, '1', expiration, null);
    }

    async checkLocalECMTicket() {
        let isLoggedIn = false;
        const ticket_ECM = localStorage.getItem(ECM_TICKET);
        if (ticket_ECM) {
            isLoggedIn = true,
            console.debug("Local ECM ticket", ticket_ECM);

            const authHeader = { 'Authorization': 'Basic ' + btoa(ticket_ECM) };
            const header = { headers: authHeader };

            // Comprobamos la validez del ticket
            try {
                const ticket = await this.http
                    .get('/alfresco/api/-default-/public/authentication/versions/1/tickets/-me-', header)
                    .toPromise();
                isLoggedIn = !!ticket;
            } catch (error) {
                console.log("ERROR", error)
                isLoggedIn = false;
            }

            if (!isLoggedIn) {
                console.debug("ECM ticket expired. Redirecting to login.");
                localStorage.removeItem(ECM_TICKET);
                
                let redirectUrl = this.route.snapshot.queryParams['redirectUrl'];
                if (!redirectUrl) {
                    redirectUrl = this.router.url;
                }

                this.router.navigate(['/login'], {
                    queryParams: { redirectUrl }
                });
            }
        }

        Promise.resolve(isLoggedIn);
    }

    /**
     * Verifica y procesa un ticket ECM recibido por URL, cookie o redirect.
     *
     * Este método valida el ticket ECM llamando al endpoint de autenticación
     * de Alfresco. Si es válido:
     *  - Oculta temporalmente el header.
     *  - Establece el ticket en el `alfrescoApiService`.
     *  - Obtiene el perfil completo del usuario y sus grupos.
     *  - Actualiza el store con la información del usuario.
     *  - Reconfigura los servicios dependientes del usuario autenticado.
     *
     * Si el ticket no es válido o ocurre un error, devuelve `false`.
     *
     * @param _url URL original de navegación.
     * @param queryParams Parámetros de la URL, donde puede venir el ticket.
     * @returns Promise<boolean> indicando si el ticket fue procesado correctamente.
     */
    private async checkECMTicket(_url: string, queryParams: Params): Promise<boolean> {
        const ticket_ECM = this.getECMTicket(queryParams);
        if (!ticket_ECM) return false;

        this.aldenService.showHeader$.next(false);

        const authHeader = { 'Authorization': 'Basic ' + btoa(ticket_ECM) };
        const header = { headers: authHeader };

        try {
            // Comprobamos la validez del ticket
            const ticket = await this.http
                .get('/alfresco/api/-default-/public/authentication/versions/1/tickets/-me-', header)
                .toPromise();
            if (!ticket) return false;

            // Seteamos el ticket en el apiService
            //console.debug("Ticket:", ticket);
            this.alfrescoApiService.getInstance().contentAuth.setTicket(ticket_ECM);

            // Obtenemos el perfil completo del usuario y lo guardamos
            const person = await this.aldenService.getPersonDetails(true);
            if (!person) return false;

            const res = await this.usersService.listGroups();
            const groupsEntries: GroupEntry[] = res.list.entries;
            const groups: Group[] = groupsEntries ? groupsEntries.map(obj => obj.entry) : [];

            //console.debug("Person", { person, groups });
            this.store.dispatch(new SetUserProfileAction({ person, groups }));

            // Configuramos la aplicación
            this.alfrescoApiService.reset();
            this.peopleContentService.resetLocalCurrentUser();
            this.alfrescoApiService.getInstance().contentAuth.saveUsername(person.id);

            return true;
        } catch (error) {
            //console.debug("Error en la verificación del ticket o al obtener grupos:", error);
            return false;
        }
    }

    /**
     * Obtiene el ticket ECM desde diferentes fuentes posibles.
     *
     * El ticket puede encontrarse en:
     *  - Una cookie temporal (`ECM_TICKET`).
     *  - Los parámetros de la URL.
     *  - Un redirect almacenado por el servicio de autenticación.
     *
     * Si se encuentra en cookie o URL, se elimina para evitar reutilización.
     *
     * @param queryParams Parámetros de la URL actual.
     * @returns El ticket ECM si existe, o `undefined` en caso contrario.
     */
    private getECMTicket(queryParams: Params) {
        // Ticket en la cookie
        let ticket_ECM = this.cookieService.getItem(ECM_TICKET);
        if (ticket_ECM) {
            this.cookieService.deleteCookie(ECM_TICKET);
        } else {
            // Ticket en la url
            ticket_ECM = queryParams?.[ECM_TICKET];
            if (ticket_ECM) {
                delete queryParams[ECM_TICKET];
            } else {
                // Ticket en el redirect
                const redirect = this.authenticationService.getRedirect();
                if (redirect && redirect.includes(ECM_TICKET)) {
                    const ticketEnRedirect = redirect.split(ECM_TICKET)[1].replace('%3D', '').replace('=', '');
                    ticket_ECM = ticketEnRedirect;
                }
            }
        }
        return ticket_ECM;
    }

}